import React, { useMemo, useEffect, useContext, useState } from 'react';
import cn from 'classnames';
import throttle from 'lodash/throttle';
import { useTranslation } from 'react-i18next';
import ddt from '@wix/da-ddt';
import {
  default as OnScrollBottom,
  DISTANCE_UNIT,
} from '@wix/da-shared-react/pkg/OnScrollBottom';
import { useStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import Pagination from '@wix/da-shared-react/pkg/Pagination';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import ArrowDown from '@wix/da-ds/pkg/Icons/16x16/ArrowDown';
import generatePaginationUrl from '../../../../utils/generatePaginationUrl';

import s from './StreamPagination.scss';

const logger = ddt.createLogger('streampagination');

const DISTANCE_FROM_BOTTOM_TO_FETCH_MORE = 1000;
const FETCH_MORE_THROTTLE = 300;

export interface Props {
  streamId: string;
  currentPage: number;
  totalPages?: number;
  totalPagesFromState?: number;
  requestUrl: string;
  isInfiniteScroll: boolean;
  isDuperbrowseActive: boolean;
  error?: string;
  children?: React.ReactNode;
}

const StreamPagination: React.FC<Props> = ({
  streamId,
  children,
  isDuperbrowseActive,
  isInfiniteScroll: isInfiniteScrollProp,
  currentPage,
  totalPages,
  totalPagesFromState = 0,
  requestUrl,
  error,
}) => {
  // force paged mode if `page` is in the querystring
  const hasPageParam =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('page');
  const isInfiniteScroll = hasPageParam ? false : isInfiniteScrollProp;

  const { t } = useTranslation();
  const { isFetching, hasMore, fetchNext } = useStream(streamId);
  logger.log(
    'StreamPagination, useStream',
    JSON.stringify({ isFetching, hasMore })
  );

  const usershop_pagination_refactor = useFlag('usershop_pagination_refactor');
  let totalPagesDisplayed = totalPages
    ? totalPages
    : hasMore
      ? currentPage + 1
      : currentPage;
  if (usershop_pagination_refactor) {
    totalPagesDisplayed = totalPagesFromState;
  }

  // TODO: remove with usershop_pagination_refactor
  logger.log(
    'StreamPagination, props',
    JSON.stringify({
      streamId,
      hasPageParam,
      isInfiniteScroll,
      currentPage,
      totalPages,
      usershop_pagination_refactor,
      totalPagesFromState,
      requestUrl,
      error,
    })
  );
  const handleScrollToBottom = useMemo(
    () =>
      throttle(() => {
        logger.log(
          'handleScrollToBottom',
          JSON.stringify({
            isFetching,
            isDuperbrowseActive,
            hasMore,
            error,
          })
        );
        if (isFetching || isDuperbrowseActive || !hasMore || error) {
          return;
        }

        fetchNext();
      }, FETCH_MORE_THROTTLE),
    [fetchNext, hasMore, isFetching, isDuperbrowseActive, error]
  );

  const isMobile = useContext(MobileContext);
  const { isLoggedIn } = useContext(SessionContext);
  const shouldRequireInfiniteScrollStart =
    isInfiniteScroll && !isLoggedIn && isMobile;
  const [mobileInfScrollStarted, setMobileInfScrollStarted] = useState(false);

  useEffect(() => {
    return () => {
      handleScrollToBottom.cancel();
    };
  }, [handleScrollToBottom]);

  return (
    <>
      {children}
      {isFetching && <LoadingIndicator />}
      {hasMore && shouldRequireInfiniteScrollStart && (
        <div
          className={cn(
            s['start-infscroll-button'],
            mobileInfScrollStarted && s['hidden']
          )}
        >
          <SolidButton
            icon={ArrowDown}
            postfixIcon={ArrowDown}
            variant={'approval-green'}
            size={'large'}
            href={generatePaginationUrl((currentPage ?? 0) + 1, requestUrl)}
            onClick={e => {
              e.preventDefault();
              setMobileInfScrollStarted(true);
            }}
          >
            {t('common.startInfiniteScroll')}
          </SolidButton>
        </div>
      )}
      {!isInfiniteScroll && (
        <div className={s['paged']}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPagesDisplayed}
            showPageNumbers={!!totalPagesDisplayed}
            getPageUrl={page => generatePaginationUrl(page, requestUrl)}
          />
        </div>
      )}
      {(mobileInfScrollStarted ||
        (isInfiniteScroll && !shouldRequireInfiniteScrollStart)) && (
        <OnScrollBottom
          checkOnMount
          checkOnUpdate={hasMore}
          onScrolledToBottom={handleScrollToBottom}
          distanceToBottom={DISTANCE_FROM_BOTTOM_TO_FETCH_MORE}
          distanceUnit={DISTANCE_UNIT.PX}
        />
      )}
    </>
  );
};
StreamPagination.displayName = 'StreamPagination';

export default StreamPagination;
